/* Reusable form field primitives for the BSCA CMS schema-driven editor.
 * All inputs are controlled. No JSON. Each field exposes a label + helper text
 * and renders a clear validation message under the input.
 */
import { useEffect, useRef, useState } from "react";
import { Plus, Trash2, Upload, ImageIcon, X, GripVertical } from "lucide-react";
import { adminApi, resolveAssetUrl } from "@/lib/api";
import { useDataLoader } from "./_useDataLoader";

const LABEL_CLS = "block bsca-eyebrow text-[#7A5A06] text-[10px] mb-1.5";
const INPUT_CLS =
  "w-full bg-white border border-black/15 px-3 py-2.5 text-sm rounded-sm focus:outline-none focus:border-[#D4AF37] disabled:bg-black/[0.03]";
const HELP_CLS = "mt-1 text-[11px] text-[#9CA3AF]";
const ERR_CLS = "mt-1 text-[11px] text-red-700";

function Label({ children, required }) {
  return (
    <div className={LABEL_CLS}>
      {children}
      {required && <span className="ml-1 text-red-600">*</span>}
    </div>
  );
}

export function TextField({ label, value, onChange, placeholder, required, error, help, type = "text", testId }) {
  return (
    <label className="block">
      <Label required={required}>{label}</Label>
      <input
        data-testid={testId}
        type={type}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className={INPUT_CLS}
      />
      {help && <div className={HELP_CLS}>{help}</div>}
      {error && <div className={ERR_CLS}>{error}</div>}
    </label>
  );
}

export function NumberField({ label, value, onChange, required, error, help, testId }) {
  return (
    <label className="block">
      <Label required={required}>{label}</Label>
      <input
        data-testid={testId}
        type="number"
        value={value ?? ""}
        onChange={(e) => {
          const v = e.target.value;
          onChange(v === "" ? "" : Number(v));
        }}
        className={INPUT_CLS}
      />
      {help && <div className={HELP_CLS}>{help}</div>}
      {error && <div className={ERR_CLS}>{error}</div>}
    </label>
  );
}

export function TextArea({ label, value, onChange, rows = 4, required, error, help, testId }) {
  return (
    <label className="block">
      <Label required={required}>{label}</Label>
      <textarea
        data-testid={testId}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        rows={rows}
        className={`${INPUT_CLS} font-sans leading-relaxed`}
      />
      {help && <div className={HELP_CLS}>{help}</div>}
      {error && <div className={ERR_CLS}>{error}</div>}
    </label>
  );
}

export function SelectField({ label, value, onChange, options, required, error, help, testId }) {
  return (
    <label className="block">
      <Label required={required}>{label}</Label>
      <select
        data-testid={testId}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        className={INPUT_CLS}
      >
        <option value="">— select —</option>
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
      {help && <div className={HELP_CLS}>{help}</div>}
      {error && <div className={ERR_CLS}>{error}</div>}
    </label>
  );
}

export function SwitchField({ label, value, onChange, help, testId }) {
  return (
    <div className="flex items-start gap-3">
      <button
        type="button"
        data-testid={testId}
        onClick={() => onChange(!value)}
        className={`mt-0.5 relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${
          value ? "bg-[#D4AF37]" : "bg-black/15"
        }`}
      >
        <span
          className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition ${
            value ? "translate-x-5" : "translate-x-0.5"
          }`}
        />
      </button>
      <div>
        <div className="text-sm font-medium text-[#050B14]">{label}</div>
        {help && <div className={HELP_CLS}>{help}</div>}
      </div>
    </div>
  );
}

/* Image picker: shows current image preview, lets user paste URL or open the
 * media library picker. */
export function ImagePickerField({ label, value, onChange, help, category, testId }) {
  const [openLib, setOpenLib] = useState(false);
  return (
    <div>
      <Label>{label}</Label>
      <div className="flex items-stretch gap-3">
        <div className="h-20 w-20 shrink-0 bg-[#F8F6F1] border border-black/10 flex items-center justify-center overflow-hidden">
          {value ? (
            <img
              src={resolveAssetUrl(value)}
              alt=""
              className="h-full w-full object-contain"
              onError={(e) => {
                e.currentTarget.style.display = "none";
              }}
            />
          ) : (
            <ImageIcon className="h-6 w-6 text-[#9CA3AF]" />
          )}
        </div>
        <div className="flex-1 min-w-0 space-y-1.5">
          <input
            data-testid={testId}
            type="text"
            value={value ?? ""}
            onChange={(e) => onChange(e.target.value)}
            placeholder="/assets/... or https://..."
            className={INPUT_CLS}
          />
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setOpenLib(true)}
              className="text-xs bsca-link inline-flex items-center gap-1 text-[#050B14]"
              data-testid={testId ? `${testId}-open-library` : undefined}
            >
              <ImageIcon className="h-3.5 w-3.5" /> Choose / upload from Library
            </button>
            {value && (
              <button
                type="button"
                onClick={() => onChange("")}
                className="text-xs text-[#9CA3AF] hover:text-red-600 inline-flex items-center gap-1"
              >
                <X className="h-3.5 w-3.5" /> Clear
              </button>
            )}
          </div>
          {help && <div className={HELP_CLS}>{help}</div>}
        </div>
      </div>
      {openLib && (
        <MediaLibraryPickerDialog
          category={category}
          onClose={() => setOpenLib(false)}
          onSelect={(asset) => {
            onChange(asset.url);
            setOpenLib(false);
          }}
        />
      )}
    </div>
  );
}

/* Inline media library picker — appears as a modal dialog. Allows direct upload
 * with category. */
export function MediaLibraryPickerDialog({ category, onSelect, onClose }) {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [q, setQ] = useState("");
  const fileRef = useRef(null);
  const cat = category || "other";

  const load = async () => {
    try {
      const data = await adminApi.assetsList({ category: cat, q: q || undefined });
      setItems(data);
    } catch (e) {
      setErr("Could not load library.");
    }
  };

  useDataLoader(load, [cat]);

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    setErr("");
    try {
      const asset = await adminApi.assetsUpload(file, cat);
      onSelect(asset);
    } catch (ex) {
      setErr(ex?.response?.data?.detail || "Upload failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[200] bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white max-w-3xl w-full max-h-[85vh] flex flex-col rounded-sm" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-black/10 flex items-center justify-between">
          <div>
            <div className="font-serif text-lg">Media Library</div>
            <div className="bsca-eyebrow text-[#7A5A06] text-[10px]">Category: {cat}</div>
          </div>
          <div className="flex items-center gap-2">
            <input
              placeholder="Search…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && load()}
              className="border border-black/15 px-3 py-1.5 text-sm rounded-sm"
            />
            <button onClick={load} className="text-xs bsca-link">Search</button>
            <input ref={fileRef} type="file" accept="image/*,application/pdf" hidden onChange={onPick} />
            <button
              onClick={() => fileRef.current?.click()}
              disabled={busy}
              data-testid="media-library-upload"
              className="bsca-lift bg-[#D4AF37] text-[#050B14] px-3 py-1.5 text-xs font-medium rounded-sm inline-flex items-center gap-1 disabled:opacity-60"
            >
              <Upload className="h-3.5 w-3.5" /> {busy ? "Uploading…" : "Upload"}
            </button>
            <button onClick={onClose} className="text-[#9CA3AF] hover:text-[#050B14]"><X className="h-4 w-4" /></button>
          </div>
        </div>
        {err && <div className="px-6 py-2 text-red-700 text-sm">{err}</div>}
        <div className="p-4 overflow-y-auto flex-1">
          {items.length === 0 ? (
            <div className="text-center py-12 text-[#9CA3AF] text-sm">No assets yet. Upload to get started.</div>
          ) : (
            <div className="grid grid-cols-3 md:grid-cols-4 gap-3">
              {items.map((a) => (
                <button
                  key={a.id}
                  data-testid={`media-asset-${a.id}`}
                  onClick={() => onSelect(a)}
                  className="group bg-[#F8F6F1] border border-black/10 hover:border-[#D4AF37] aspect-square overflow-hidden flex flex-col"
                  title={a.original_name}
                >
                  <div className="flex-1 flex items-center justify-center overflow-hidden">
                    {a.mime?.startsWith("image/") ? (
                      <img src={resolveAssetUrl(a.url)} alt="" className="h-full w-full object-cover" />
                    ) : (
                      <span className="text-xs text-[#4B5563] p-3 text-center break-all">{a.original_name}</span>
                    )}
                  </div>
                  <div className="px-2 py-1.5 text-[10px] truncate text-[#4B5563]">{a.original_name}</div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* Repeatable list of rows. Each row is rendered by `renderRow(item, update, index)`.
 * `defaultItem` is appended when "Add" is clicked. Supports basic ordering via arrows. */
export function RepeatableRows({ label, items, onChange, defaultItem, renderRow, help, testId, addLabel = "Add" }) {
  const list = Array.isArray(items) ? items : [];
  const update = (i, patch) => {
    const next = list.slice();
    next[i] = typeof patch === "function" ? patch(next[i]) : { ...next[i], ...patch };
    onChange(next);
  };
  const remove = (i) => {
    const next = list.slice();
    next.splice(i, 1);
    onChange(next);
  };
  const move = (i, dir) => {
    const next = list.slice();
    const j = i + dir;
    if (j < 0 || j >= next.length) return;
    const [it] = next.splice(i, 1);
    next.splice(j, 0, it);
    onChange(next);
  };
  return (
    <div data-testid={testId}>
      <Label>{label}</Label>
      <div className="space-y-3">
        {list.map((item, i) => (
          <div key={i} className="border border-black/10 rounded-sm bg-[#FBFAF6]">
            <div className="flex items-center justify-between px-3 py-2 border-b border-black/5">
              <div className="flex items-center gap-2 text-xs text-[#4B5563]">
                <GripVertical className="h-3.5 w-3.5 text-[#9CA3AF]" />
                #{i + 1}
              </div>
              <div className="flex items-center gap-2">
                <button type="button" onClick={() => move(i, -1)} className="text-xs text-[#4B5563] hover:text-[#050B14]" title="Move up">↑</button>
                <button type="button" onClick={() => move(i, 1)} className="text-xs text-[#4B5563] hover:text-[#050B14]" title="Move down">↓</button>
                <button type="button" onClick={() => remove(i)} className="text-xs text-[#9CA3AF] hover:text-red-600 inline-flex items-center gap-1">
                  <Trash2 className="h-3 w-3" /> Remove
                </button>
              </div>
            </div>
            <div className="p-3">{renderRow(item, (patch) => update(i, patch), i)}</div>
          </div>
        ))}
        <button
          type="button"
          onClick={() => onChange([...(list || []), { ...(defaultItem || {}) }])}
          data-testid={testId ? `${testId}-add` : undefined}
          className="bsca-lift inline-flex items-center gap-2 text-xs bg-white border border-black/15 px-3 py-2 rounded-sm hover:border-[#D4AF37]"
        >
          <Plus className="h-3.5 w-3.5" /> {addLabel}
        </button>
        {help && <div className={HELP_CLS}>{help}</div>}
      </div>
    </div>
  );
}

/* Key/Value link table (used for retail_links, social_links, etc.). Keys can be
 * preset; values are URLs. */
export function LinkMap({ label, value, onChange, keys, help, testId }) {
  const obj = value && typeof value === "object" ? value : {};
  const setKey = (k, v) => {
    const next = { ...obj };
    if (v === "" || v == null) {
      delete next[k];
    } else {
      next[k] = v;
    }
    onChange(next);
  };
  return (
    <div data-testid={testId}>
      <Label>{label}</Label>
      <div className="space-y-2">
        {keys.map((k) => (
          <div key={k.value} className="grid grid-cols-12 gap-2 items-center">
            <div className="col-span-3 text-xs text-[#4B5563]">{k.label}</div>
            <div className="col-span-9">
              <input
                value={obj[k.value] ?? ""}
                onChange={(e) => setKey(k.value, e.target.value)}
                placeholder={k.placeholder || "https://…"}
                className={INPUT_CLS}
              />
            </div>
          </div>
        ))}
      </div>
      {help && <div className={HELP_CLS}>{help}</div>}
    </div>
  );
}

/* Validation helpers */
export function isValidUrl(v) {
  if (!v) return true;
  try {
    if (v.startsWith("/")) return true;
    return !!new URL(v);
  } catch {
    return false;
  }
}
export function isValidEmail(v) {
  if (!v) return true;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
}
export function isValidSlug(v) {
  if (!v) return true;
  return /^[a-z0-9-]+$/.test(v);
}
